import { useEffect, useMemo, useState } from 'react';

import GalleryService from '@/services/gallery.service';

const GALLERY_ID_KEYS = ['_id', 'id', 'gallery_id', 'galleryId'];

function normalizeGalleryId(candidate, visited = new Set()) {
  if (!candidate) return null;

  if (typeof candidate === 'string' || typeof candidate === 'number') {
    const value = String(candidate).trim();
    return value.length > 0 ? value : null;
  }

  if (typeof candidate !== 'object') return null;
  if (visited.has(candidate)) return null;
  visited.add(candidate);

  for (const key of GALLERY_ID_KEYS) {
    if (candidate[key]) {
      const resolved = normalizeGalleryId(candidate[key], visited);
      if (resolved) return resolved;
    }
  }

  if (candidate.gallery) {
    const resolved = normalizeGalleryId(candidate.gallery, visited);
    if (resolved) return resolved;
  }

  if (candidate.user_gallery) {
    const resolved = normalizeGalleryId(candidate.user_gallery, visited);
    if (resolved) return resolved;
  }

  return null;
}

export function resolveGalleryIdFromCandidates(candidates) {
  const values = Array.isArray(candidates) ? candidates : [candidates];

  for (const candidate of values) {
    const resolved = normalizeGalleryId(candidate);
    if (resolved) return resolved;
  }

  return null;
}

export default function useGalleryStickers(rawCandidates) {
  const galleryId = useMemo(
    () => resolveGalleryIdFromCandidates(rawCandidates),
    [rawCandidates],
  );
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!galleryId) {
      if (isMounted) {
        setStickers([]);
        setLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    setLoading(true);

    GalleryService.getStickers(galleryId)
      .then(({ response, data }) => {
        if (!isMounted) return;

        if (response.ok) {
          let stickersData = [];

          if (Array.isArray(data)) {
            stickersData = data;
          } else if (Array.isArray(data?.data)) {
            stickersData = data.data;
          } else if (Array.isArray(data?.stickers)) {
            stickersData = data.stickers;
          } else if (Array.isArray(data?.stickers?.data)) {
            stickersData = data.stickers.data;
          } else if (Array.isArray(data?.results)) {
            stickersData = data.results;
          } else if (Array.isArray(data?.results?.data)) {
            stickersData = data.results.data;
          }

          setStickers(stickersData);
        } else {
          setStickers([]);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setStickers([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [galleryId]);

  return { galleryId, stickers, stickersLoading: loading };
}